import { Card } from 'components'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { amountFormatter, formatPropertyTitle } from 'utils'
import {
  Flex,
  VStack,
  Heading,
  Image,
  useColorModeValue,
  Text,
  useMediaQuery,
  Badge,
  Skeleton,
  HStack,
  Center,
  useDisclosure,
  Link,
  Box
} from '@chakra-ui/react'
import { Property } from 'generated/graphql'
import moment from 'moment'
import { FavoriteProperty, PropertyStatInfos, SlideShowModal } from '..'
import { VscCalendar } from 'react-icons/vsc'

import { Swiper, SwiperSlide } from 'swiper/react'

// import Swiper core and required modules
import SwiperCore, { Navigation, Thumbs } from 'swiper/core'
import { iconSizes, theme } from 'theme'
import { useGeolocation } from 'hooks'
import CustomTooltip from 'components/CustomTooltip'

SwiperCore.use([Navigation, Thumbs])

type PropertyResultCardProps = {
  property: Property | undefined
  isLoaded: boolean
  onClick: () => void
  expanded?: boolean
}

const PropertyResultCard: React.FC<PropertyResultCardProps> = ({
  property,
  isLoaded: loading,
  onClick,
  expanded
}) => {
  const [isTabletOrMobile] = useMediaQuery('(max-width: 50em)')
  const { t, i18n } = useTranslation()
  const tanslator = i18n as any
  const lang = tanslator.language as string
  const bg = useColorModeValue('white', 'gray.700')
  const priceColor = useColorModeValue('orange.500', 'orange.100')
  const bottomBorderColor = useColorModeValue('gray.100', 'gray.700')
  const { isOpen, onClose } = useDisclosure()
  const isNew = moment().diff(moment(property?.published_at), 'days') < 2
  const { currency } = useGeolocation()
  const [thumbsSwiper] = useState<SwiperCore | null | undefined>(null)
  const agencyLogo = property?.agent?.agency?.logo?.url
  return expanded ? (
    <Card
      shouldAnimate
      border="none"
      pb={4}
      bg="transparent"
      shadow="none"
      cursor="pointer"
      width="100%"
    >
      <Flex align="flex-start" direction="column" borderRadius="xl">
        <Flex width="100%" onClick={onClick} direction="column">
          <Skeleton zIndex={0} isLoaded={loading}>
            <Flex borderRadius="xl" width="100%" overflow="hidden" flex={1}>
              <Swiper
                slidesPerView={1}
                spaceBetween={0}
                thumbs={{ swiper: thumbsSwiper }}
                className="cardSwiper"
                pagination={{
                  clickable: true,
                  dynamicBullets: true
                }}
              >
                {property?.photos?.map((photo) => (
                  <SwiperSlide key={photo?.id}>
                    <Skeleton
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      minH="100%"
                      isLoaded={loading}
                    >
                      <Image
                        flex={1}
                        objectFit="cover"
                        alt="nsoko property, property image"
                        maxH="100%"
                        minH={isTabletOrMobile ? 300 : 250}
                        fallback={<Skeleton width="100%" borderRadius="xl" height={210} />}
                        fallbackSrc={photo?.formats?.thumbnail?.url}
                        src={
                          photo?.formats?.small?.url ?? photo?.formats?.medium?.url ?? photo?.url
                        }
                      />
                    </Skeleton>
                  </SwiperSlide>
                ))}
              </Swiper>
            </Flex>
          </Skeleton>
          <VStack
            mt={{ base: 4 }}
            p={0}
            spacing={2}
            flex={1}
            justify="space-between"
            align="flex-start"
          >
            <VStack spacing={0} align="flex-start">
              <Flex justify="space-between" direction={['column', 'row']} width="100%">
                <Skeleton isLoaded={loading}>
                  <Link
                    mt={1}
                    display="block"
                    fontSize="sm"
                    lineHeight={1.5}
                    fontWeight="bold"
                    mr={[0, 2]}
                  >
                    {formatPropertyTitle(property as Property, t, lang)}
                  </Link>
                </Skeleton>
              </Flex>
              {property?.suburb?.name && (
                <Skeleton isLoaded={loading}>
                  <Text fontSize="xs" color="gray.500" fontWeight="semibold">
                    {property?.suburb?.name}, {property?.suburb?.city?.name}
                  </Text>
                </Skeleton>
              )}
              <Skeleton mt={[2, 0]} isLoaded={loading}>
                <Center>
                  <VscCalendar size={iconSizes.md} />
                  <Text fontSize="xs" color="gray.500" mx={2}>
                    {t('search_results.available_on')}{' '}
                    {moment(property?.occupation_date).format('DD MMMM YYYY')}
                  </Text>
                </Center>
              </Skeleton>

              <Skeleton isLoaded={loading}>
                <Flex align="center">
                  <Heading
                    fontWeight="extrabold"
                    textTransform="uppercase"
                    size="sm"
                    letterSpacing="wide"
                    mr={2}
                    color={priceColor}
                    as="h5"
                  >
                    {amountFormatter(property?.price as number, currency)}
                  </Heading>{' '}
                  <Text fontWeight="semibold" fontSize="x-small">
                    {(property?.rental_term as any)?.[`name_${lang}`]}
                  </Text>
                  {isNew && (
                    <Badge mx={2} fontSize="xx-small" colorScheme="green">
                      {t('search_results.new')}
                    </Badge>
                  )}
                </Flex>
              </Skeleton>
            </VStack>
            <Box>
              {agencyLogo ? (
                <CustomTooltip label={property?.agent?.agency?.name || ''}>
                  <Image
                    w={{
                      base: 12,
                      md: 24
                    }}
                    py={3}
                    rounded="lg"
                    objectFit="contain"
                    src={property?.agent?.agency?.logo?.url}
                  />
                </CustomTooltip>
              ) : (
                <></>
              )}
            </Box>
            <HStack spacing={3} fontSize="xs">
              <PropertyStatInfos propertyData={property as Property} loading={!loading} />
            </HStack>
          </VStack>
        </Flex>
      </Flex>
      <SlideShowModal
        isOpen={isOpen}
        onClose={onClose}
        property={property as Property}
        loading={!loading}
      />
      <FavoriteProperty
        position="absolute"
        right={0}
        m={[8, 4, 4]}
        propertyData={property as Property}
      />
    </Card>
  ) : (
    <Card
      border="none"
      boxShadow={theme.boxShadow2}
      borderBottomColor={bottomBorderColor}
      p={4}
      rounded="md"
      bg={bg}
      shadow="none"
      cursor="pointer"
      width="100%"
    >
      <Flex width="100%" onClick={onClick} direction={isTabletOrMobile ? 'column' : 'row'}>
        <Flex
          width="100%"
          maxW={160}
          h="100%"
          minW={90}
          overflow="hidden"
          transition="all 0.2s ease-in-out"
          _hover={{
            transform: 'scale(1.5)',
            rounded: 'xl'
          }}
          flex={1}
        >
          <Swiper
            slidesPerView={1}
            autoHeight={true}
            spaceBetween={0}
            thumbs={{ swiper: thumbsSwiper }}
            className="cardSwiper"
            pagination={{
              clickable: true,
              dynamicBullets: true
            }}
          >
            {property?.photos?.map((photo) => (
              <SwiperSlide key={photo?.id}>
                <Skeleton
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  isLoaded={loading}
                >
                  <Image
                    flex={1}
                    objectFit="cover"
                    rounded="md"
                    alt="nsoko property, property image"
                    maxH="100%"
                    fallback={<Skeleton width="100%" borderRadius="xl" height={110} />}
                    fallbackSrc={photo?.formats?.thumbnail?.url}
                    src={photo?.formats?.small?.url ?? photo?.formats?.medium?.url ?? photo?.url}
                  />
                </Skeleton>
              </SwiperSlide>
            ))}
          </Swiper>
        </Flex>
        <VStack
          mt={{ base: 4, md: 0 }}
          ml={{ md: 4 }}
          p={[4, 0, 0]}
          spacing={1}
          flex={1}
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Flex w="100%" justify="space-between">
            <VStack spacing={0} align="flex-start">
              <Skeleton isLoaded={loading}>
                <Flex align="center">
                  <Heading
                    fontWeight="extrabold"
                    textTransform="uppercase"
                    size="sm"
                    lineHeight={0.5}
                    letterSpacing="wide"
                    mr={2}
                    color={priceColor}
                    as="h5"
                  >
                    {amountFormatter(property?.price as number, currency)}
                  </Heading>
                  <Badge px={1} rounded="md" fontSize="xx-small">
                    {(property?.rental_term as any)?.[`name_${lang}`]}
                  </Badge>
                  {isNew && (
                    <Badge mx={2} px={1} rounded="md" fontSize="xx-small" colorScheme="green">
                      {t('search_results.new')}
                    </Badge>
                  )}
                </Flex>
              </Skeleton>
              <Flex justify="space-between" direction={['column', 'row']} width="100%">
                <Skeleton isLoaded={loading}>
                  <Link
                    mt={1}
                    display="block"
                    lineHeight={1.4}
                    fontSize="xs"
                    fontWeight="bold"
                    mr={[0, 2]}
                  >
                    {formatPropertyTitle(property as Property, t, lang)}
                  </Link>
                </Skeleton>
              </Flex>

              <Skeleton mt={[2, 0]} isLoaded={loading}>
                <Center>
                  <VscCalendar size={iconSizes.md} />
                  <Text fontSize="x-small" fontWeight="semibold" mx={2}>
                    {t('search_results.available_on')}{' '}
                    {moment(property?.occupation_date).format('DD MMMM YYYY')}
                  </Text>
                </Center>
              </Skeleton>
            </VStack>
          </Flex>
          {agencyLogo ? (
            <CustomTooltip label={property?.agent?.agency?.name || ''}>
              <Image
                w={{
                  base: 12,
                  md: 16
                }}
                py={3}
                rounded="lg"
                objectFit="contain"
                src={property?.agent?.agency?.logo?.url}
              />
            </CustomTooltip>
          ) : (
            <></>
          )}
          <HStack spacing={3} mt={2} fontSize="xs">
            <PropertyStatInfos propertyData={property as Property} loading={!loading} />
          </HStack>
        </VStack>
      </Flex>
    </Card>
  )
}
PropertyResultCard.defaultProps = {
  expanded: true
}

export default PropertyResultCard
